import { ClientsAnalytics, ClientAnalytics } from '@/types';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Fetch aggregate analytics for all clients
 */
export async function getClientsAnalytics(token: string): Promise<ClientsAnalytics> {
  const response = await fetch(`${API_URL}/analytics/clients`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch analytics' }));
    throw new Error(error.error || 'Failed to fetch analytics');
  }

  return response.json();
}

/**
 * Fetch analytics for a specific client
 */
export async function getClientAnalytics(
  clientId: string,
  token: string
): Promise<ClientAnalytics> {
  const response = await fetch(`${API_URL}/analytics/clients/${clientId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch client analytics' }));
    throw new Error(error.error || 'Failed to fetch client analytics');
  }

  return response.json();
}

/**
 * Fetch analytics for multiple clients in parallel
 */
export async function getMultipleClientAnalytics(
  clientIds: string[],
  token: string
): Promise<Map<string, ClientAnalytics>> {
  const requests = clientIds.map((id) => getClientAnalytics(id, token));
  const results = await Promise.all(requests);

  const analyticsMap = new Map<string, ClientAnalytics>();
  results.forEach((analytics) => {
    analyticsMap.set(analytics.clientId, analytics);
  });

  return analyticsMap;
}
